import React, { useState, useEffect, useRef } from 'react';
import './ProductsSupported.css';

function ProductsSupported() {
  // Define the financial/accounting products we support
  const supportedProducts = [
    {
      name: "Stripe",
      logoUrl: "/images/products/stripe.png",
      bgColor: "#635aff"
    },
    {
      name: "Stripe Atlas",
      logoUrl: "/images/products/stripe-atlas.jpg",
      bgColor: "#ffffff"
    },
    {
      name: "Gusto",
      logoUrl: "/images/products/gusto.png",
      bgColor: "#f4624d"
    },
    {
      name: "Mercury",
      logoUrl: "/images/products/mercury.png",
      bgColor: "#d3d6e8"
    },
    {
      name: "Brex",
      logoUrl: "/images/products/brex.png",
      bgColor: "#f87340"
    },
    {
      name: "Deel",
      logoUrl: "/images/products/deel.png",
      bgColor: "#ffffff"
    },
    {
      name: "ADP - Payroll",
      logoUrl: "/images/products/adp.jpg",
      bgColor: "#ffffff"
    },
    {
      name: "Rippling",
      logoUrl: "/images/products/rippling.jpg",
      bgColor: "#ffffff"
    },
    {
      name: "MainStreet",
      logoUrl: "/images/products/mainstreet.jpg",
      bgColor: "#ffffff"
    },
    {
      name: "ADP - R&D Tax Credits",
      logoUrl: "/images/products/adp-tax.png",
      bgColor: "#ffffff"
    },
    {
      name: "Carta",
      logoUrl: "/images/products/carta.png",
      bgColor: "#e2edf2"
    },
    {
      name: "QuickBooks",
      logoUrl: "/images/products/quickbooks.png",
      bgColor: "#2CA01C"
    },
    {
      name: "Xero",
      logoUrl: "/images/products/xero.png",
      bgColor: "#13B5EA"
    },
    {
      name: "FreshBooks",
      logoUrl: "/images/products/freshbooks.png",
      bgColor: "#0075DD"
    },
    {
      name: "Sage",
      logoUrl: "/images/products/sage.png",
      bgColor: "#00D639"
    },
    {
      name: "Wave Accounting",
      logoUrl: "/images/products/wave.png",
      bgColor: "#1C76E2"
    },
    {
      name: "NetSuite",
      logoUrl: "/images/products/netsuite.png",
      bgColor: "#144A98"
    },
    {
      name: "Zoho Books",
      logoUrl: "/images/products/zoho-books.png",
      bgColor: "#EC262E"
    },
    {
      name: "Drake Tax",
      logoUrl: "/images/products/drake.png",
      bgColor: "#00529B"
    },
    {
      name: "TurboTax",
      logoUrl: "/images/products/turbotax.png",
      bgColor: "#00A79D"
    },
    {
      name: "TaxAct",
      logoUrl: "/images/products/taxact.png",
      bgColor: "#E4002B"
    },
    {
      name: "H&R Block",
      logoUrl: "/images/products/hrblock.png",
      bgColor: "#00843D"
    },
    {
      name: "ProSeries",
      logoUrl: "/images/products/proseries.png",
      bgColor: "#006AB6"
    },
    {
      name: "Lacerte",
      logoUrl: "/images/products/lacerte.png",
      bgColor: "#005EB8"
    },
    {
      name: "UltraTax CS",
      logoUrl: "/images/products/ultratax.png",
      bgColor: "#ED1C24"
    },
    {
      name: "CCH Axcess",
      logoUrl: "/images/products/cch-axcess.png",
      bgColor: "#009245"
    }
  ];

  // Track which product images have failed to load
  const [failedImages, setFailedImages] = useState({});
  
  // Track which product images have been loaded
  const [loadedImages, setLoadedImages] = useState({});
  
  // Refs for the product cards
  const productRefs = useRef([]);

  useEffect(() => {
    // Create an intersection observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const productIndex = parseInt(entry.target.dataset.index);
          const product = supportedProducts[productIndex];
          
          // Mark this product as loaded (or ready to load)
          setLoadedImages(prev => ({
            ...prev,
            [product.name]: true
          }));
          
          // Unobserve the element once it's been loaded
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '100px', // Start loading when image is 100px from viewport
      threshold: 0.1 // Trigger when at least 10% of the item is visible
    });
    
    // Observe all product cards
    productRefs.current.forEach(ref => {
      if (ref) {
        observer.observe(ref);
      }
    });
    
    // Clean up observer
    return () => {
      productRefs.current.forEach(ref => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, [supportedProducts]);

  const handleImageError = (productName) => {
    setFailedImages(prev => ({
      ...prev,
      [productName]: true
    }));
  };

  return (
    <div className="products-supported-page">
      <div className="products-hero">
        <div className="container">
          <h1 className="products-title">Products We Support</h1>
          <p className="products-subtitle">
            Leverage our expertise with these financial and accounting platforms to streamline your operations
          </p>
        </div>
      </div>
      
      <div className="products-content container">
        <div className="products-grid">
          {supportedProducts.map((product, index) => (
            <div 
              key={index} 
              className="product-card"
              ref={el => productRefs.current[index] = el}
              data-index={index}
            >
              {/* <div className="redeem-text">Request support for this product</div> */}
              <div 
                className="product-logo" 
                style={{ backgroundColor: product.bgColor }}
              >
                {!failedImages[product.name] ? (
                  loadedImages[product.name] ? (
                    <img 
                      src={product.logoUrl} 
                      alt={`${product.name} logo`} 
                      className="product-image"
                      onError={() => handleImageError(product.name)}
                    />
                  ) : (
                    <div className="image-placeholder"></div>
                  )
                ) : (
                  <div className="product-fallback">{product.name}</div>
                )}
              </div>
              <div className="product-info">
                <h5 className="product-name">{product.name}</h5>
              </div>
            </div>
          ))}
        </div>
        
        <div className="info-section">
          <h2>How We Help You</h2>
          <p>
            Our team of experts can assist you with setting up, configuring, and optimizing these 
            financial and accounting platforms for your business needs. We provide implementation 
            support, training, and ongoing assistance to ensure your financial operations run smoothly.
          </p>
          <p>
            Contact us to learn more about how we can help you leverage these powerful tools for your business.
          </p>
          <a href="/contact" className="contact-button">Get Support</a>
        </div>
      </div>
    </div>
  );
}

export default ProductsSupported; 